import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Observable } from 'rxjs';
import { Injectable } from '@angular/core';
import { Consts } from '@shared/consts';
import { FeatureToggle } from '@shared/enums';
import { isFeatureToggleActive } from 'app/featuresToggleSettings/feature-toggle-storage';

interface RoutingDataStorage {
	serverUrl?: string;
	doctorBffApis?: string[];
	doctorBffWdUrl?: string;
}

@Injectable({
	providedIn: 'root'
})
export class DoctorBffRoutingInterceptor implements HttpInterceptor {

	private cachedRoutingData: RoutingDataStorage | null = null;
	private lastRoutesJson: string | null = null;

	intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
		if (isFeatureToggleActive(FeatureToggle.DisableDoctorBFFRollout)) {
			return next.handle(request);
		}

		const routingData = this.getRoutingData();
		if (!routingData?.doctorBffApis?.length || !routingData.doctorBffWdUrl || !routingData.serverUrl) {
			return next.handle(request);
		}

		const rewrittenUrl = this.tryRewriteUrl(
			request.url,
			routingData.serverUrl,
			routingData.doctorBffWdUrl,
			routingData.doctorBffApis
		);
		if (rewrittenUrl) {
			request = request.clone({ url: rewrittenUrl });
		}

		return next.handle(request);
	}

	private tryRewriteUrl(url: string, serverUrl: string, doctorBffWdUrl: string, doctorBffApis: string[]): string | null {
		// Extract relative path - handle both absolute URLs and relative URLs
		let relativePathWithSlash: string;

		if (this.matchesServerUrl(url, serverUrl)) {
			// Absolute URL matching our server (with segment-boundary check).
			// Normalize the trailing slash so extraction works whether or not
			// serverUrl includes one.
			const normalizedServer = serverUrl.endsWith('/') ? serverUrl.slice(0, -1) : serverUrl;
			const relativePath = url.substring(normalizedServer.length);
			relativePathWithSlash = relativePath.startsWith('/') ? relativePath : '/' + relativePath;
		} else if (url.startsWith('/')) {
			// Relative URL (e.g., from baseUrl interceptor)
			relativePathWithSlash = url;
		} else {
			// URL doesn't match our server and isn't relative
			return null;
		}

		// Check if this path matches any of the doctorBffApis using segment-boundary matching
		const hasMatchingApi = doctorBffApis.some(api => this.matchesApiPath(relativePathWithSlash, api));

		if (!hasMatchingApi) {
			return null;
		}

		// Rewrite the URL to use doctorBffWdUrl
		const baseUrl = doctorBffWdUrl.endsWith('/') ? doctorBffWdUrl.slice(0, -1) : doctorBffWdUrl;
		return baseUrl + relativePathWithSlash;
	}

	/**
	 * Checks if the URL starts with the serverUrl using a segment-boundary check.
	 * Prevents false positives like https://myapp.com/api matching https://myapp.com/apiv2/...
	 */
	private matchesServerUrl(url: string, serverUrl: string): boolean {
		// Normalize: strip a single trailing slash so the boundary check works
		// whether or not serverUrl includes one.
		const normalizedServer = serverUrl.endsWith('/') ? serverUrl.slice(0, -1) : serverUrl;

		const lowerUrl = url.toLowerCase();
		const lowerServer = normalizedServer.toLowerCase();

		if (!lowerUrl.startsWith(lowerServer)) {
			return false;
		}

		// The character immediately after the prefix must be '/', '?', '#', or end-of-string
		const nextChar = url[normalizedServer.length];
		return nextChar === undefined || nextChar === '/' || nextChar === '?' || nextChar === '#';
	}

	/**
	 * Matches a request path against an API path using segment-boundary matching.
	 * Prevents false positives like /mvc/orders matching /mvc/orders-archive.
	 * Allows query params and fragments as valid boundaries (e.g., /mvc/orders?page=1).
	 */
	private matchesApiPath(requestPath: string, apiPath: string): boolean {
		const normalizedApi = (apiPath.startsWith('/') ? apiPath : '/' + apiPath).toLowerCase();
		const normalizedPath = requestPath.toLowerCase();

		if (normalizedPath === normalizedApi) {
			return true;
		}

		if (!normalizedPath.startsWith(normalizedApi)) {
			return false;
		}

		// The character immediately after the API prefix must be a segment boundary
		const nextChar = normalizedPath[normalizedApi.length];
		return nextChar === '/' || nextChar === '?' || nextChar === '#';
	}

	private getRoutingData(): RoutingDataStorage | null {
		try {
			const routesJson = localStorage.getItem(Consts.Storage.Routes);

			// Return cached data if localStorage hasn't changed
			if (routesJson === this.lastRoutesJson) {
				return this.cachedRoutingData;
			}

			this.lastRoutesJson = routesJson;

			if (!routesJson) {
				this.cachedRoutingData = null;
				return null;
			}

			const parsed = JSON.parse(routesJson);

			// Validate the shape of the parsed data
			if (!this.isValidRoutingData(parsed)) {
				console.error('[DoctorBffRoutingInterceptor] Invalid routing data shape in localStorage:', parsed);
				this.cachedRoutingData = null;
				return null;
			}

			this.cachedRoutingData = parsed;
			return this.cachedRoutingData;
		} catch (error) {
			console.error('[DoctorBffRoutingInterceptor] Failed to parse routing data from localStorage:', error);
			this.cachedRoutingData = null;
			return null;
		}
	}

	/**
	 * Runtime validation of RoutingDataStorage shape to guard against
	 * stale or malformed data from localStorage.
	 */
	private isValidRoutingData(data: unknown): data is RoutingDataStorage {
		if (typeof data !== 'object' || data === null) {
			return false;
		}

		const obj = data as Record<string, unknown>;

		// serverUrl must be undefined or a string
		if (obj.serverUrl !== undefined && typeof obj.serverUrl !== 'string') {
			return false;
		}

		// doctorBffWdUrl must be undefined or a string
		if (obj.doctorBffWdUrl !== undefined && typeof obj.doctorBffWdUrl !== 'string') {
			return false;
		}

		// doctorBffApis must be undefined or an array of strings
		if (obj.doctorBffApis !== undefined) {
			if (!Array.isArray(obj.doctorBffApis)) {
				return false;
			}
			if (!obj.doctorBffApis.every(item => typeof item === 'string')) {
				return false;
			}
		}

		return true;
	}
}
